import styled, { css } from 'styled-components';
import colors, { opacity } from 'styles/colors';
import { FieldContainer, FieldInputWrapper } from 'components/InfoField/styles';

import SCREEN from 'styles/screen';

export const SMALL_COLUMN_WIDTH = 285;
export const MEDIUM_COLUMN_WIDTH = 285;
export const COLUMN_MARGIN = 20;

export const COLUMN_WIDTH_MIXIN = css`
  width: ${SMALL_COLUMN_WIDTH}px;

  @media (min-width: ${SCREEN.MEDIUM}) {
    width: ${MEDIUM_COLUMN_WIDTH}px;
  }
`;

export const HALF_COLUMN_WIDTH_MIXIN = css`
  margin-right: 0;
  width: ${SMALL_COLUMN_WIDTH / 2 - COLUMN_MARGIN / 2}px;

  @media (min-width: ${SCREEN.MEDIUM}) {
    width: ${MEDIUM_COLUMN_WIDTH / 2 - COLUMN_MARGIN / 2}px;
  }
`;

export const DOUBLE_COLUMN_WIDTH_MIXIN = css`
  width: ${2 * SMALL_COLUMN_WIDTH}px;

  @media (min-width: ${SCREEN.MEDIUM}) {
    width: ${2 * MEDIUM_COLUMN_WIDTH}px;
  }
`;

export const Container = styled.div`
  background: ${colors.white};
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);

  h2 {
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 600;
  }

  hr {
    border: 0;
    border-top: 1px solid ${colors.gray80};
    margin-bottom: 22px;
    margin-top: 22px;
    width: 100%;
  }
`;

export const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 19px;
`;

export const Body = styled.div`
  min-height: 50px;
  height: 100%;
`;

export const HeaderWrapper = styled.div`
  align-items: center;
  border-bottom: 2px solid ${colors.gray80};
  display: flex;
  justify-content: space-between;
  min-height: 50px;
  padding: 0 19px;
  width: 100%;

  button {
    min-width: 102px;
  }
`;

export const Footer = styled.div`
  border-top: 1px solid ${colors.gray70};
  padding: 25px 34px 23px 34px;

  h4 {
    margin-top: 0;
  }
`;

export const ColumnGroup = styled.div`
  display: flex;

  h3 {
    font-size: 14px;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 15px;
    flex: 0 0 100%;
  }

  h4 {
    margin-top: 0;
  }

  ${FieldContainer} {
    flex: 0 0 100%;
  }

  ${FieldInputWrapper} {
    margin-right: 0;
  }
`;

export const RowGroup = styled(ColumnGroup)`
  display: flex;
  flex-direction: column;
`;

export const ToggleContent = styled.div`
  ${ColumnGroup} {
    margin-top: 22px;
  }
`;

export const SectionToggle = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
`;

export const Column = styled.div`
  ${COLUMN_WIDTH_MIXIN};

  align-content: start;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;

  margin-right: ${COLUMN_MARGIN}px;

  &:last-child {
    margin-right: 0;
  }
`;

export const StackedColumns = styled.div`
  ${Column} {
    margin-top: 30px;

    &:first-child {
      margin-top: 0;
    }
  }
`;

export const Row = styled(Column)`
  align-items: start;
  justify-content: start;
  width: 100%;

  @media (min-width: ${SCREEN.MEDIUM}) {
    width: 100%;
  }

  ${FieldContainer} {
    &:not(:first-of-type) {
      margin-left: ${COLUMN_MARGIN}px;
    }
    flex: 0 0 auto;
    ${COLUMN_WIDTH_MIXIN};
  }
`;

export const Section = styled.section`
  border: 1px solid ${colors.fleetOffGray};
  border-radius: 4px;
  box-shadow: 0px 2px 6px 0px ${colors.shadowBorder};
  background-color: ${colors.white};
  height: 100%;

  ${({ placeholder = false }) =>
    placeholder &&
    `
    border: 2px dashed ${colors.fleetOffGray};
    background-color: ${opacity(colors.fleetOffGray, 0.2)};
    box-shadow: none;
  `}

  & + & {
    // if sibling of itself
    margin-top: 16px;
  }
`;

const HEADER_HEIGHT = '56px';

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  flex-basis: ${HEADER_HEIGHT};
  border-bottom: 1px solid ${colors.fleetOffGray};
`;

export const CompactSectionHeader = styled(SectionHeader)`
  padding: 12px 16px 14px;
  border-bottom: none;
`;

export const SectionHeaderTitle = styled.div`
  flex-grow: 1;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  align-items: center;
  line-height: 32px;

  gap: 8px;
`;

export const SectionHeaderActions = styled.div`
  & > *:nth-child(n + 2) {
    margin-left: 8px;
  }
`;

export const SubSection = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    margin-top: 24px;
  }
`;

export const SectionContent = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  height: calc(100% - ${HEADER_HEIGHT});

  & ${SubSection} + ${SubSection} {
    margin-top: 24px;
  }
`;

export const SubSectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  font-size: 14px;
  line-height: 19px;
  font-weight: 600;
  margin-bottom: 4px;
`;

export const SectionFooter = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px solid ${colors.fleetOffGray};
`;
